import { SEASON_SEGMENT_NAMES } from '../../Constants/season'
import { SeasonType } from '../../Routing/urls'
import { callGraphQL } from '../utils/request'
import {
  allPlayersWithStatsQuery,
  fetchAllTimeLeaderByStat,
  fetchAllPlayersAdvancedStats,
  allStatLeadersQuery,
  singlePlayerForComparison,
} from './players.queries'
import type {
  AllPlayersResponse,
  PlayerData,
  AllTimeLeaderResponse,
  AllTimePlayerInfo,
  AllPlayersAdvancedStatsResponse,
  PlayerWithAdvancedStatsData,
  LeagueLeader,
  LeagueLeadersResponse,
  PlayerComparisonResponse,
  PlayerComparisonData,
} from './players.types'

export async function getAllPlayers(
  season: number,
  seasonSegment: SeasonType,
  limit?: number,
  league?: string
): Promise<PlayerData[]> {
  try {
    const data = await callGraphQL<AllPlayersResponse>(allPlayersWithStatsQuery, {
      season,
      includeZPP: seasonSegment === SEASON_SEGMENT_NAMES.champseries,
      includeReg: seasonSegment === SEASON_SEGMENT_NAMES.regular,
      includePost: seasonSegment === SEASON_SEGMENT_NAMES.post,
      limit,
      league: league ?? 'PLL',
    })
    return data?.allPlayers ?? []
  } catch (error) {
    console.error('getAllPlayers error', error)
    return []
  }
}

/**
 * Fetch team leaders by stat
 */
export async function fetchLeagueLeaders(
  year: number,
  seasonSegment: string,
  statList: string[],
  limit: number = 3,
  league?: string
): Promise<LeagueLeader[]> {
  try {
    const leaders = await callGraphQL<LeagueLeadersResponse>(allStatLeadersQuery, {
      year,
      seasonSegment,
      statList,
      limit,
      league: league ?? 'PLL',
    })

    if (!leaders?.playerStatLeaders) {
      return []
    }
    return leaders.playerStatLeaders
  } catch (err) {
    console.error('fetchLeagueLeaders error:', err)
    return []
  }
}

/**
 * Fetch player comparison data for a specific player and year
 */
export async function fetchAllTimeLeaderByStatGQL(
  stat: string,
  limit: number
): Promise<AllTimePlayerInfo[]> {
  try {
    const data = await callGraphQL<AllTimeLeaderResponse>(fetchAllTimeLeaderByStat, {
      stat: stat,
      limit: limit,
    })
    return data.careerStats
  } catch (err) {
    console.error('fetchAllTimeLeaderByStatGQL error:', err)
    return []
  }
}

/**
 * Fetch all time stats leaders
 */
export async function fetchAllPlayersAdvancedStatsGQL(
  year: number,
  limit: number = 250
): Promise<PlayerWithAdvancedStatsData[]> {
  try {
    const data = await callGraphQL<AllPlayersAdvancedStatsResponse>(
      fetchAllPlayersAdvancedStats,
      {
        year,
        limit,
      }
    )
    return data?.allPlayers || []
  } catch (err) {
    console.error('fetchAllPlayersAdvancedStatsGQL error:', err)
    return []
  }
}

/**
 * Fetch all time stats leaders
 */
export async function fetchPlayerComparisonData(
  year: number,
  officialId: string
): Promise<PlayerComparisonData | null> {
  try {
    const data = await callGraphQL<PlayerComparisonResponse>(singlePlayerForComparison, {
      id: officialId,
      year: year,
      statsYear: year,
    })

    if (!data.player) {
      return null
    }
    return data.player
  } catch (err) {
    console.error('fetchPlayerComparisonData error:', err)
    return null
  }
}
